import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { apiGet } from '../../api/client';
import { Button, Card } from '../../design-system';
import { tokens } from '../../design-system/tokens';

type UsageDay = {
  date: string;
  requests: number;
  durationMs: number;
  cpuMs: number;
  responseBytes: number;
  resultCount: number;
  fargateUsd: number;
  rdsUsd: number;
};

type UsageEndpoint = {
  endpoint: string;
  requests: number;
  success: number;
  status400: number;
  status401: number;
  status429: number;
  durationMs: number;
  cpuMs: number;
  responseBytes: number;
  resultCount: number;
};

type UsageReport = {
  from: string;
  to: string;
  lastRequestAt: string | null;
  totals: {
    requests: number;
    success: number;
    status400: number;
    status401: number;
    status429: number;
    durationMs: number;
    cpuMs: number;
    p50DurationMs: number;
    p95DurationMs: number;
    responseBytes: number;
    resultCount: number;
  };
  cost: {
    totalUsd: number;
    fargateUsd: number;
    rdsUsd: number;
    error: string | null;
    vcpu: number;
    taskCount: number;
  };
  byEndpoint: UsageEndpoint[];
  byDay: UsageDay[];
};

type ChartSeries = {
  label: string;
  color: string;
  value: (day: UsageDay) => number;
};

const RANGES = [7, 30, 90] as const;
const CHART_W = 640;
const CHART_H = 220;
const PAD_L = 52;
const PAD_R = 12;
const PAD_T = 16;
const PAD_B = 32;

function formatUsd(value: number): string {
  if (value === 0) return 'US$ 0';
  if (Math.abs(value) < 0.01) return `US$ ${value.toFixed(6)}`;
  return value.toLocaleString('es-UY', { style: 'currency', currency: 'USD' });
}

function formatBytes(value: number): string {
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(value: number): string {
  if (value < 1000) return `${Math.round(value)} ms`;
  if (value < 60_000) return `${(value / 1000).toFixed(1)} s`;
  return `${(value / 60_000).toFixed(1)} min`;
}

function formatCount(value: number): string {
  return Math.round(value).toLocaleString('es-UY');
}

function formatDay(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}`;
}

function axisNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(value >= 10_000 ? 0 : 1)}k`;
  if (value >= 10) return String(Math.round(value));
  if (value >= 1) return value.toFixed(1);
  if (value === 0) return '0';
  return value.toFixed(3);
}

const cell: CSSProperties = { padding: '6px 8px', textAlign: 'left', fontSize: 13 };
const numberCell: CSSProperties = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function DailyChart({
  title,
  indicator,
  note,
  days,
  series,
  formatValue,
  formatAxis = axisNumber,
}: {
  title: string;
  indicator: string;
  note?: string;
  days: UsageDay[];
  series: ChartSeries[];
  formatValue: (value: number) => string;
  formatAxis?: (value: number) => string;
}) {
  const plotW = CHART_W - PAD_L - PAD_R;
  const plotH = CHART_H - PAD_T - PAD_B;
  const slot = days.length > 0 ? plotW / days.length : plotW;
  const totals = days.map((day) => series.reduce((sum, item) => sum + item.value(day), 0));
  const max = Math.max(1, ...totals);
  const labelStep = days.length > 60 ? 14 : days.length > 20 ? 5 : 1;
  const ticks = [max, max / 2, 0];

  return (
    <Card variant="outlined" padding="md">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: tokens.colors.neutral[800] }}>{title}</h3>
        <strong style={{ fontSize: 18, color: tokens.colors.neutral[900], fontVariantNumeric: 'tabular-nums' }}>{indicator}</strong>
      </div>
      {note && <p style={{ margin: '6px 0 0', fontSize: 12, color: tokens.colors.neutral[500] }}>{note}</p>}
      {series.length > 1 && (
        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          {series.map((item) => (
            <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: tokens.colors.neutral[600] }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
      )}
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} role="img" aria-label={title} style={{ width: '100%', height: 'auto', marginTop: 8 }}>
        {ticks.map((tick) => {
          const y = PAD_T + plotH - (tick / max) * plotH;
          return (
            <g key={tick}>
              <line x1={PAD_L} x2={CHART_W - PAD_R} y1={y} y2={y} stroke={tokens.colors.neutral[200]} strokeWidth="1" />
              <text x={PAD_L - 6} y={y + 3} textAnchor="end" fill={tokens.colors.neutral[500]} fontSize="11">
                {formatAxis(tick)}
              </text>
            </g>
          );
        })}
        {days.map((day, index) => {
          let stacked = 0;
          const x = PAD_L + index * slot + slot * 0.14;
          const width = Math.max(1, slot * 0.72);
          return (
            <g key={day.date}>
              {series.map((item) => {
                const amount = item.value(day);
                const height = (amount / max) * plotH;
                const y = PAD_T + plotH - ((stacked + amount) / max) * plotH;
                stacked += amount;
                return (
                  <rect key={item.label} x={x} y={y} width={width} height={Math.max(amount > 0 ? 1 : 0, height)} fill={item.color} rx="1">
                    <title>{`${formatDay(day.date)} · ${item.label}: ${formatValue(amount)}`}</title>
                  </rect>
                );
              })}
              {index % labelStep === 0 && (
                <text x={x + width / 2} y={CHART_H - 8} textAnchor="middle" fill={tokens.colors.neutral[500]} fontSize="10">
                  {formatDay(day.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </Card>
  );
}

export function ImpactoUsagePanel({ agentId, token }: { agentId: string; token: string | null }) {
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    const to = new Date();
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    try {
      const data = await apiGet<UsageReport>(
        `/api/agents/${agentId}/plugins/impacto-intelligence/usage?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
        token,
      );
      setReport(data);
    } catch (e) {
      setReport(null);
      setError(e instanceof Error ? e.message : 'No se pudo leer el uso');
    } finally {
      setLoading(false);
    }
  }, [agentId, token, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = report?.totals;
  const byDay = report?.byDay ?? [];

  return (
    <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, color: tokens.colors.neutral[900] }}>Panel de control</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.colors.neutral[600] }}>
            Uso y procesamiento de la API. El costo es la parte de Fargate y RDS que corresponde a este agente.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {RANGES.map((range) => (
            <Button
              key={range}
              type="button"
              variant={days === range ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setDays(range)}
              disabled={loading}
            >
              {range} días
            </Button>
          ))}
        </div>
      </div>

      {error && <p style={{ margin: 0, fontSize: 13, color: tokens.colors.error.dark }}>{error}</p>}

      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <Metric label="Llamadas" value={formatCount(totals.requests)} />
          <Metric label="Éxito" value={formatCount(totals.success)} />
          <Metric label="400 / 401 / 429" value={`${totals.status400} / ${totals.status401} / ${totals.status429}`} />
          <Metric label="Tiempo" value={formatDuration(totals.durationMs)} />
          <Metric label="CPU" value={formatDuration(totals.cpuMs)} />
          <Metric label="p50 / p95" value={`${formatDuration(totals.p50DurationMs)} / ${formatDuration(totals.p95DurationMs)}`} />
          <Metric label="Bytes" value={formatBytes(totals.responseBytes)} />
          <Metric label="Filas" value={formatCount(totals.resultCount)} />
          <Metric
            label="Costo AWS"
            value={report?.cost.error ? 'Sin dato' : formatUsd(report?.cost.totalUsd ?? 0)}
          />
        </div>
      )}

      {report?.cost.error && <p style={{ margin: 0, fontSize: 13, color: tokens.colors.error.dark }}>{report.cost.error}</p>}
      {report && !report.cost.error && (
        <p style={{ margin: 0, fontSize: 13, color: tokens.colors.neutral[600] }}>
          Fargate {formatUsd(report.cost.fargateUsd)} · RDS {formatUsd(report.cost.rdsUsd)} · capacidad {report.cost.vcpu} vCPU × {report.cost.taskCount}{' '}
          {report.cost.taskCount === 1 ? 'tarea' : 'tareas'}.
        </p>
      )}

      {report && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
          <DailyChart
            title="Llamadas por día"
            indicator={formatCount(totals?.requests ?? 0)}
            days={byDay}
            series={[{ label: 'Llamadas', color: tokens.colors.primary[500], value: (day) => day.requests }]}
            formatValue={formatCount}
          />
          <DailyChart
            title="Transferencia de bytes por día"
            indicator={formatBytes(totals?.responseBytes ?? 0)}
            days={byDay}
            series={[{ label: 'Bytes', color: tokens.colors.navy[500], value: (day) => day.responseBytes }]}
            formatValue={formatBytes}
            formatAxis={formatBytes}
          />
          <DailyChart
            title="Tiempo de procesamiento por día"
            indicator={formatDuration(totals?.durationMs ?? 0)}
            days={byDay}
            series={[{ label: 'Tiempo', color: tokens.colors.warning.main, value: (day) => day.durationMs }]}
            formatValue={formatDuration}
            formatAxis={formatDuration}
          />
          <DailyChart
            title="CPU por día"
            indicator={formatDuration(totals?.cpuMs ?? 0)}
            days={byDay}
            series={[{ label: 'CPU', color: tokens.colors.brand[600], value: (day) => day.cpuMs }]}
            formatValue={formatDuration}
            formatAxis={formatDuration}
          />
          <DailyChart
            title="Costo AWS por día"
            indicator={report.cost.error ? 'Sin dato' : formatUsd(report.cost.totalUsd)}
            note="Fargate según CPU. RDS según el tiempo de consulta."
            days={byDay}
            series={[
              { label: 'Fargate', color: tokens.colors.primary[600], value: (day) => day.fargateUsd },
              { label: 'RDS', color: tokens.colors.brand[700], value: (day) => day.rdsUsd },
            ]}
            formatValue={formatUsd}
            formatAxis={(value) => (value === 0 ? '0' : value < 0.01 ? value.toFixed(4) : value.toFixed(2))}
          />
        </div>
      )}

      {report && report.byEndpoint.length > 0 && (
        <Card variant="outlined" padding="md">
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: tokens.colors.neutral[800] }}>Detalle por endpoint</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: tokens.colors.neutral[600] }}>
                  <th style={cell}>Endpoint</th>
                  <th style={numberCell}>Llamadas</th>
                  <th style={numberCell}>Éxito</th>
                  <th style={numberCell}>400</th>
                  <th style={numberCell}>401</th>
                  <th style={numberCell}>429</th>
                  <th style={numberCell}>Tiempo</th>
                  <th style={numberCell}>CPU</th>
                  <th style={numberCell}>Bytes</th>
                  <th style={numberCell}>Filas</th>
                </tr>
              </thead>
              <tbody>
                {report.byEndpoint.map((row) => (
                  <tr key={row.endpoint} style={{ borderTop: `1px solid ${tokens.colors.neutral[200]}` }}>
                    <td style={cell}>{row.endpoint}</td>
                    <td style={numberCell}>{formatCount(row.requests)}</td>
                    <td style={numberCell}>{formatCount(row.success)}</td>
                    <td style={numberCell}>{row.status400}</td>
                    <td style={numberCell}>{row.status401}</td>
                    <td style={numberCell}>{row.status429}</td>
                    <td style={numberCell}>{formatDuration(row.durationMs)}</td>
                    <td style={numberCell}>{formatDuration(row.cpuMs)}</td>
                    <td style={numberCell}>{formatBytes(row.responseBytes)}</td>
                    <td style={numberCell}>{formatCount(row.resultCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Card variant="outlined" padding="sm">
      <div style={{ fontSize: 12, color: tokens.colors.neutral[500] }}>{label}</div>
      <div style={{ marginTop: 4, fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[800], fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </Card>
  );
}
